import React, { useMemo } from "react";
import { Platform, StyleSheet, View } from "react-native";
import variables from "../../styles/variables";
import Gap from "../Gap";
import Icon from "../Icon";
import RText from "../RText";
import { RLink } from "../RLink/RLink";

/**
 * Live Button Link component
 *
 * This is virtually identical to LiveButton.tsx, except its a link, i.e onPress takes you to watch, rather than
 * attempting to play something
 *
 * @param {boolean} openPlayChannel - Whether to open the Play TV channel specifically
 *
 * @returns {JSX.Element} - The rendered Live Button Link component
 *
 */

export default function LiveButtonLink({
  openPlayChannel = false,
}: {
  openPlayChannel: boolean;
}) {
  const hoverRef = React.useRef();

  const handleMouseEnter = () => {
    if (hoverRef.current && Platform.OS === "web") {
      hoverRef.current.style.backgroundColor =
        variables.colors.palette.buttons.default.hovered.backgroundColor;
    }
  };

  const handleMouseLeave = () => {
    if (hoverRef.current && Platform.OS === "web") {
      hoverRef.current.style.backgroundColor =
        variables.colors.palette.rtv.secondary; // Revert style
    }
  };

  const link = openPlayChannel
    ? { screen: "Watch", params: { channel: "play-tv" } }
    : { screen: "Watch" };

  // TODO - fix this warning:
  //   Warning: Function components cannot be given refs. Attempts to access this ref will fail. Did you mean to use React.forwardRef()?

  // Check the render method of `ForwardRef`.
  //     in RText
  //     in Unknown (created by Button)
  //     in Button (created by LiveButtonLink)
  const Button = () =>
    useMemo(() => {
      return (
        <RLink
          to={link}
          ref={hoverRef}
          customTextStyle={{ display: "none" }}
          // @ts-ignore
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <View style={[styles.buttonRound]}>
            <RText
              family="secondary"
              weight="bold"
              size="md"
              color="white"
              style={styles.liveText}
            >
              Live
            </RText>
            <Gap size="xxsmall" />
            <Icon name="play" size="medium" />
          </View>
        </RLink>
      );
    }, []);

  return <Button />;
}

const styles = StyleSheet.create({
  buttonRound: {
    backgroundColor: variables.colors.palette.rtv.secondary,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 36,
    borderWidth: 1.5,
    borderColor: variables.colors.palette.rtv.secondary,
  },
  liveText: {
    textTransform: "uppercase",
    paddingRight: 0,
    paddingLeft: 8,
  },
});
